import Phaser from 'phaser';
import { GAMEPLAY_CONFIG } from '../../app/config';
import { calculateDifficulty } from '../../domain/difficulty/DifficultyModel';
import { GameplaySession } from '../../domain/gameplay/GameplaySession';
import { GameRunTracker, type GameRunStats } from '../../domain/gameplay/GameRunStats';
import {
  PowerUpSpawnManager,
  type PowerUpSpawnDebug,
  type PowerUpType,
} from '../../domain/powerups/PowerUpSpawnManager';
import { ScoreService } from '../../domain/scoring/ScoreService';
import { AppState } from '../../domain/state/AppState';
import type { GameStateMachine } from '../../domain/state/GameStateMachine';
import type { KeyboardInputAdapter } from '../../input/KeyboardInputAdapter';
import type { AudioSystem } from '../../services/AudioSystem';
import type { EventBus } from '../../services/EventBus';
import type { RandomSource } from '../../services/RandomSource';
import type { StorageService } from '../../services/StorageService';
import { ASTEROID_VARIANTS, type AsteroidVariantConfig } from '../config/asteroidVariants';
import { drawAsteroidGraphics } from '../rendering/AsteroidGraphicsRenderer';
import type {
  AsteroidEntity,
  BulletEntity,
  FloatingTextEntity,
  PowerUpEntity,
  SparkEntity,
} from '../entities';

type TouchState = { axis: { x: number; y: number }; fire: boolean };

export type GameWorldSnapshot = {
  score: number;
  lives: number;
  ammo: number;
  maxAmmo: number;
  activeAsteroids: number;
  activeBullets: number;
  activePowerUps: number;
  asteroidVariantIds: string[];
  powerUpDebug: PowerUpSpawnDebug;
  run: GameRunStats;
};

export class GameWorld {
  private readonly bullets: BulletEntity[];
  private readonly asteroids: AsteroidEntity[];
  private readonly sparks: SparkEntity[];
  private readonly powerUps: PowerUpEntity[];
  private readonly floatingTexts: FloatingTextEntity[];
  private readonly debugGraphics: Phaser.GameObjects.Graphics;
  private readonly session = new GameplaySession();
  private readonly run = new GameRunTracker();
  private readonly powerUpSpawns: PowerUpSpawnManager;
  private readonly recentVariantIds: string[] = [];
  private velocityX = 0;
  private velocityY = 0;
  private elapsedMs = 0;
  private spawnElapsedMs = 0;
  private lastFireMs = -Infinity;
  private lastEmptySoundMs = -Infinity;
  private clockMs = 0;
  private invulnerableUntil = 0;
  private score: ScoreService;
  private running = false;

  public constructor(
    private readonly scene: Phaser.Scene,
    private readonly ship: Phaser.GameObjects.Container,
    private readonly input: KeyboardInputAdapter,
    private readonly touch: TouchState,
    private readonly machine: GameStateMachine,
    private readonly events: EventBus,
    private readonly storage: StorageService,
    private readonly audio: AudioSystem,
    private readonly random: RandomSource,
    highScore: number,
  ) {
    this.score = new ScoreService(highScore);
    this.powerUpSpawns = new PowerUpSpawnManager(random);
    this.bullets = Array.from({ length: GAMEPLAY_CONFIG.bullet.maxActive }, () => ({
      view: scene.add.rectangle(-40, -40, 8, 3, 0x8ff8ff).setDepth(20).setVisible(false),
      active: false,
      x: 0,
      y: 0,
    }));
    this.asteroids = Array.from({ length: GAMEPLAY_CONFIG.asteroid.maxActiveMax }, () => ({
      view: scene.add.graphics().setDepth(16).setVisible(false),
      active: false,
      x: 0,
      y: 0,
      radius: 0,
      speed: 0,
      hp: 0,
      maxHp: 0,
      rotation: 0,
      spin: 0,
      passed: false,
      destroyed: false,
      kind: 'medium',
      variantId: '',
      hitboxWidth: 0,
      hitboxHeight: 0,
      hitboxOffsetX: 0,
      hitboxOffsetY: 0,
    }));
    this.sparks = Array.from({ length: 48 }, () => ({
      view: scene.add.rectangle(-40, -40, 2, 2, 0xffb45f).setDepth(22).setVisible(false),
      active: false,
      velocityX: 0,
      velocityY: 0,
      lifeMs: 0,
    }));
    this.powerUps = Array.from({ length: 2 }, () => ({
      view: this.createPowerUpView(),
      active: false,
      type: 'ammo',
      x: 0,
      baseY: 0,
      phase: 0,
      collected: false,
      spawnedAt: 0,
    }));
    this.floatingTexts = Array.from({ length: 6 }, () => ({
      view: scene.add
        .text(-60, -60, '', {
          fontFamily: 'Courier New, monospace',
          fontSize: '9px',
          color: '#f6f3d4',
          stroke: '#050714',
          strokeThickness: 3,
        })
        .setOrigin(0.5)
        .setDepth(28)
        .setVisible(false),
      active: false,
      lifeMs: 0,
    }));
    this.debugGraphics = scene.add.graphics().setDepth(99).setVisible(false);
  }

  public start(): void {
    this.run.start();
    this.running = true;
    this.emitHud('reset');
  }

  public reset(): void {
    this.running = false;
    this.velocityX = 0;
    this.velocityY = 0;
    this.elapsedMs = 0;
    this.spawnElapsedMs = 0;
    this.clockMs = 0;
    this.lastFireMs = -Infinity;
    this.lastEmptySoundMs = -Infinity;
    this.invulnerableUntil = 0;
    this.score.reset();
    this.session.reset();
    this.run.reset();
    this.powerUpSpawns.reset();
    this.recentVariantIds.length = 0;
    this.bullets.forEach((entity) => this.deactivateBullet(entity));
    this.asteroids.forEach((entity) => this.deactivateAsteroid(entity));
    this.sparks.forEach((entity) => this.deactivateSpark(entity));
    this.powerUps.forEach((entity) => this.deactivatePowerUp(entity, false));
    this.floatingTexts.forEach((entity) => this.deactivateFloatingText(entity));
    this.emitHud('reset');
  }

  public update(deltaInput: number): void {
    const delta = Math.min(deltaInput, 40);
    if (!this.running) {
      this.updateSparks(delta);
      this.updateFloatingTexts(delta);
      return;
    }
    const state = this.machine.state;
    if (state !== AppState.Playing && state !== AppState.Hit) return;
    this.clockMs += delta;
    this.elapsedMs += delta;
    const difficulty = calculateDifficulty(this.elapsedMs, this.score.score);
    const input = this.input.snapshot(this.touch.axis, this.touch.fire);
    this.updatePlayer(input.axisX, input.axisY, delta);
    if (input.fireHeld && this.clockMs - this.lastFireMs >= GAMEPLAY_CONFIG.weapon.fireCooldownMs) {
      this.tryFire();
    }
    this.spawnElapsedMs += delta;
    if (
      this.spawnElapsedMs >= difficulty.spawnIntervalMs &&
      this.activeAsteroids < difficulty.maxActiveAsteroids
    ) {
      this.spawnElapsedMs = 0;
      this.spawnAsteroid(difficulty.worldSpeed);
    }
    const gameplay = this.session.snapshot;
    const requestedPowerUp = this.powerUpSpawns.update(delta, {
      activeCount: this.activePowerUps,
      ammo: gameplay.ammo,
      maxAmmo: gameplay.maxAmmo,
      lives: gameplay.lives,
      maxLives: gameplay.maxLives,
    });
    if (requestedPowerUp && !this.spawnPowerUp(requestedPowerUp)) this.powerUpSpawns.defer();
    this.updateBullets(delta);
    this.updateAsteroids(delta, difficulty.worldSpeed);
    this.updatePowerUps(delta);
    this.updateSparks(delta);
    this.updateFloatingTexts(delta);
    this.handleCollisions();
    this.updateDebugHitboxes();
    if (state === AppState.Hit && this.clockMs >= this.invulnerableUntil) {
      this.ship.setAlpha(1);
      this.machine.transition(AppState.Playing);
    } else if (state === AppState.Hit) {
      this.ship.setAlpha(Math.floor(this.clockMs / 90) % 2 === 0 ? 0.48 : 1);
    }
  }

  public get snapshot(): GameWorldSnapshot {
    const gameplay = this.session.snapshot;
    return {
      score: this.score.score,
      lives: gameplay.lives,
      ammo: gameplay.ammo,
      maxAmmo: gameplay.maxAmmo,
      activeAsteroids: this.activeAsteroids,
      activeBullets: this.bullets.filter((item) => item.active).length,
      activePowerUps: this.activePowerUps,
      asteroidVariantIds: this.asteroids
        .filter((item) => item.active)
        .map((item) => item.variantId),
      powerUpDebug: this.powerUpSpawns.debug,
      run: this.run.snapshot,
    };
  }

  public damageForTest(): void {
    this.takeDamage();
  }

  public gameOverForTest(): void {
    this.session.setLivesForTest(1);
    this.invulnerableUntil = 0;
    this.takeDamage();
  }

  public setAmmoForTest(ammo: number): void {
    this.session.setAmmoForTest(ammo);
    this.emitAmmo('reset');
  }

  public spawnPowerUpForTest(type: PowerUpType): void {
    this.spawnPowerUp(type, true);
  }

  public collectPowerUpForTest(type: PowerUpType): void {
    const active = this.powerUps.find((item) => item.active && item.type === type);
    if (active) this.collectPowerUp(active);
  }

  public toggleDebugHitboxes(): boolean {
    const visible = !this.debugGraphics.visible;
    this.debugGraphics.setVisible(visible);
    if (!visible) this.debugGraphics.clear();
    return visible;
  }

  private updatePlayer(axisX: number, axisY: number, delta: number): void {
    const seconds = delta / 1000;
    let normalizedX = axisX;
    let normalizedY = axisY;
    if (axisX !== 0 && axisY !== 0) {
      normalizedX *= Math.SQRT1_2;
      normalizedY *= Math.SQRT1_2;
    }
    const approach = (value: number, target: number, step: number): number =>
      value < target ? Math.min(value + step, target) : Math.max(value - step, target);
    const targetX = normalizedX * GAMEPLAY_CONFIG.player.maxSpeed;
    const targetY = normalizedY * GAMEPLAY_CONFIG.player.maxSpeed;
    const rate = axisX === 0 ? GAMEPLAY_CONFIG.player.drag : GAMEPLAY_CONFIG.player.acceleration;
    const rateY = axisY === 0 ? GAMEPLAY_CONFIG.player.drag : GAMEPLAY_CONFIG.player.acceleration;
    this.velocityX = approach(this.velocityX, targetX, rate * seconds);
    this.velocityY = approach(this.velocityY, targetY, rateY * seconds);
    this.ship.x = Phaser.Math.Clamp(this.ship.x + this.velocityX * seconds, 32, 384);
    this.ship.y = Phaser.Math.Clamp(this.ship.y + this.velocityY * seconds, 24, 246);
    this.ship.rotation = Phaser.Math.Linear(this.ship.rotation, axisY * 0.14, 0.16);
  }

  private tryFire(): void {
    this.lastFireMs = this.clockMs;
    const bullet = this.bullets.find((item) => !item.active);
    if (!bullet) return;
    if (!this.session.consumeShot()) {
      const gameplay = this.session.snapshot;
      if (this.clockMs - this.lastEmptySoundMs >= GAMEPLAY_CONFIG.weapon.emptySoundCooldownMs) {
        this.lastEmptySoundMs = this.clockMs;
        this.audio.play('empty');
        this.events.emit('player:ammo-empty', { ammo: 0, maxAmmo: gameplay.maxAmmo });
        this.events.emit('ui:feedback', { message: 'AMMO EMPTY', tone: 'warning' });
      }
      return;
    }
    bullet.active = true;
    bullet.x = this.ship.x + 26;
    bullet.y = this.ship.y;
    bullet.view.setPosition(bullet.x, bullet.y).setVisible(true);
    const gameplay = this.session.snapshot;
    this.events.emit('player:shot', { ammo: gameplay.ammo, maxAmmo: gameplay.maxAmmo });
    this.run.recordShot();
    this.emitAmmo('shot');
    this.audio.play('laser');
    if (gameplay.ammo === GAMEPLAY_CONFIG.weapon.lowAmmoThreshold) {
      this.audio.play('lowAmmo');
      this.events.emit('ui:feedback', { message: 'LOW AMMO', tone: 'warning' });
    }
  }

  private updateBullets(delta: number): void {
    const seconds = delta / 1000;
    for (const bullet of this.bullets) {
      if (!bullet.active) continue;
      bullet.x += GAMEPLAY_CONFIG.bullet.speed * seconds;
      bullet.view.x = Math.round(bullet.x);
      if (bullet.x > 512) this.deactivateBullet(bullet);
    }
  }

  private spawnAsteroid(worldSpeed: number): void {
    const asteroid = this.asteroids.find((item) => !item.active);
    if (!asteroid) return;
    const variant = this.selectAsteroidVariant();
    asteroid.active = true;
    asteroid.kind = variant.sizeClass;
    asteroid.variantId = variant.id;
    asteroid.hitboxWidth = variant.hitbox.width;
    asteroid.hitboxHeight = variant.hitbox.height;
    asteroid.hitboxOffsetX = variant.hitbox.offsetX;
    asteroid.hitboxOffsetY = variant.hitbox.offsetY;
    asteroid.radius = Math.max(variant.hitbox.width, variant.hitbox.height) / 2 + 3;
    asteroid.hp = variant.health;
    asteroid.maxHp = variant.health;
    asteroid.speed = worldSpeed * variant.speedMultiplier * (0.92 + this.random.next() * 0.16);
    asteroid.x = 510 + this.random.next() * 35;
    const minY = 28 + asteroid.radius;
    const maxY = 242 - asteroid.radius;
    let y = minY + this.random.next() * (maxY - minY);
    let last: AsteroidEntity | undefined;
    for (let index = this.asteroids.length - 1; index >= 0; index -= 1) {
      const candidate = this.asteroids[index];
      if (candidate?.active && candidate !== asteroid) {
        last = candidate;
        break;
      }
    }
    if (last && Math.abs(last.y - y) < asteroid.radius + last.radius + 18) {
      y = y < 135 ? maxY : minY;
    }
    asteroid.y = y;
    asteroid.rotation = this.random.next() * Math.PI * 2;
    asteroid.spin = Phaser.Math.Linear(
      variant.rotationSpeedRange.min,
      variant.rotationSpeedRange.max,
      this.random.next(),
    );
    asteroid.passed = false;
    asteroid.destroyed = false;
    drawAsteroidGraphics(asteroid.view, variant);
    asteroid.view.setPosition(asteroid.x, asteroid.y).setVisible(true);
    this.recentVariantIds.push(variant.id);
    if (this.recentVariantIds.length > 2) this.recentVariantIds.shift();
  }

  private selectAsteroidVariant(): AsteroidVariantConfig {
    const weights = ASTEROID_VARIANTS.map((variant) =>
      this.recentVariantIds.includes(variant.id) ? variant.weight * 0.12 : variant.weight,
    );
    const total = weights.reduce((sum, weight) => sum + weight, 0);
    let roll = this.random.next() * total;
    for (let index = 0; index < ASTEROID_VARIANTS.length; index += 1) {
      roll -= weights[index] ?? 0;
      if (roll <= 0) return ASTEROID_VARIANTS[index] ?? ASTEROID_VARIANTS[0]!;
    }
    return ASTEROID_VARIANTS[0]!;
  }

  private updateAsteroids(delta: number, worldSpeed: number): void {
    const seconds = delta / 1000;
    for (const asteroid of this.asteroids) {
      if (!asteroid.active) continue;
      asteroid.x -= Math.max(asteroid.speed, worldSpeed * 0.72) * seconds;
      asteroid.rotation += asteroid.spin * seconds;
      asteroid.view.setPosition(Math.round(asteroid.x), Math.round(asteroid.y));
      asteroid.view.rotation = asteroid.rotation;
      if (!asteroid.passed && asteroid.x + asteroid.radius < this.ship.x - 19) {
        asteroid.passed = this.score.asteroidPassed(asteroid.passed, asteroid.destroyed);
        if (asteroid.passed) {
          this.run.recordMeteorPassed();
          this.emitScore();
        }
      }
      if (asteroid.x < -45) this.deactivateAsteroid(asteroid);
    }
  }

  private spawnPowerUp(type: PowerUpType, force = false): boolean {
    const powerUp = this.powerUps.find((item) => !item.active);
    if (!powerUp || this.activePowerUps >= GAMEPLAY_CONFIG.powerUps.maximumActive) return false;
    let y = 50 + this.random.next() * 170;
    let safe = false;
    for (let attempt = 0; attempt < 5; attempt += 1) {
      safe = this.asteroids.every(
        (asteroid) =>
          !asteroid.active ||
          Phaser.Math.Distance.Between(520, y, asteroid.x, asteroid.y) >=
            GAMEPLAY_CONFIG.powerUps.safeObstacleDistance,
      );
      if (safe) break;
      y = 50 + this.random.next() * 170;
    }
    if (!safe && !force) return false;
    powerUp.active = true;
    powerUp.type = type;
    powerUp.x = force ? this.ship.x : 520;
    powerUp.baseY = force ? this.ship.y : y;
    powerUp.phase = this.random.next() * Math.PI * 2;
    powerUp.collected = false;
    powerUp.spawnedAt = this.clockMs;
    this.drawPowerUp(powerUp);
    powerUp.view.setVisible(true).setAlpha(1).setScale(1).setPosition(powerUp.x, powerUp.baseY);
    this.events.emit('powerup:spawned', { type });
    return true;
  }

  private createPowerUpView(): Phaser.GameObjects.Container {
    const graphics = this.scene.add.graphics();
    return this.scene.add.container(-50, -50, [graphics]).setDepth(19).setVisible(false);
  }

  private drawPowerUp(powerUp: PowerUpEntity): void {
    const graphics = powerUp.view.getAt(0) as Phaser.GameObjects.Graphics;
    graphics.clear();
    if (powerUp.type === 'ammo') {
      graphics.fillStyle(0x163c4e).fillRect(-8, -8, 16, 16);
      graphics.fillStyle(0x55d9e8).fillRect(-6, -6, 12, 12);
      graphics.fillStyle(0xe9fbef).fillTriangle(-3, -4, 5, 0, -3, 4);
      graphics.fillStyle(0x1a7085).fillRect(-8, -3, 3, 6);
    } else {
      graphics.fillStyle(0x4f1f47).fillRect(-8, -7, 16, 14);
      graphics.fillStyle(0xff7182).fillRect(-5, -4, 10, 8);
      graphics.fillStyle(0xffcad0).fillRect(-2, -6, 4, 12);
      graphics.fillStyle(0xffcad0).fillRect(-6, -2, 12, 4);
      graphics.fillStyle(0x8d2f5f).fillRect(-8, -2, 3, 5);
    }
  }

  private updatePowerUps(delta: number): void {
    const seconds = delta / 1000;
    for (const powerUp of this.powerUps) {
      if (!powerUp.active) continue;
      const speed = powerUp.type === 'ammo' ? 70 : 58;
      const amplitude = powerUp.type === 'ammo' ? 6 : 9;
      powerUp.x -= speed * seconds;
      const age = this.clockMs - powerUp.spawnedAt;
      const y = powerUp.baseY + Math.sin(powerUp.phase + age * 0.004) * amplitude;
      powerUp.view.setPosition(Math.round(powerUp.x), Math.round(y));
      powerUp.view.rotation = Math.sin(powerUp.phase + age * 0.003) * 0.08;
      const pulse = 1 + (Math.floor(age / 160) % 2) * 0.08;
      powerUp.view.setScale(pulse);
      if (powerUp.x < -24) this.deactivatePowerUp(powerUp, true);
    }
  }

  private handleCollisions(): void {
    for (const bullet of this.bullets) {
      if (!bullet.active) continue;
      const target = this.asteroids.find(
        (asteroid) => asteroid.active && this.pointInsideAsteroid(bullet.x, bullet.y, asteroid),
      );
      if (!target) continue;
      this.deactivateBullet(bullet);
      target.hp -= 1;
      this.burst(target.x, target.y, 4, 0xc7b4d4);
      if (target.hp <= 0) {
        target.destroyed = true;
        this.score.asteroidDestroyed();
        this.run.recordMeteorDestroyed();
        this.emitScore();
        this.audio.play('explosion');
        this.burst(target.x, target.y, target.kind === 'large' ? 14 : 8, 0x9d91a8);
        this.deactivateAsteroid(target);
      } else {
        target.view.setAlpha(0.62);
      }
    }
    for (const powerUp of this.powerUps) {
      if (
        powerUp.active &&
        Phaser.Math.Distance.Between(this.ship.x, this.ship.y, powerUp.view.x, powerUp.view.y) < 22
      ) {
        this.collectPowerUp(powerUp);
      }
    }
    if (this.clockMs < this.invulnerableUntil) return;
    const collision = this.asteroids.find(
      (asteroid) => asteroid.active && this.playerOverlapsAsteroid(asteroid),
    );
    if (collision) {
      this.burst(collision.x, collision.y, 10, 0xff7182);
      this.deactivateAsteroid(collision);
      this.takeDamage();
    }
  }

  private pointInsideAsteroid(x: number, y: number, asteroid: AsteroidEntity): boolean {
    const dx = (x - (asteroid.x + asteroid.hitboxOffsetX)) / Math.max(1, asteroid.hitboxWidth / 2);
    const dy = (y - (asteroid.y + asteroid.hitboxOffsetY)) / Math.max(1, asteroid.hitboxHeight / 2);
    return dx * dx + dy * dy <= 1;
  }

  private playerOverlapsAsteroid(asteroid: AsteroidEntity): boolean {
    const dx =
      (this.ship.x - (asteroid.x + asteroid.hitboxOffsetX)) / (19 + asteroid.hitboxWidth / 2);
    const dy =
      (this.ship.y - (asteroid.y + asteroid.hitboxOffsetY)) / (7 + asteroid.hitboxHeight / 2);
    return dx * dx + dy * dy <= 1;
  }

  private collectPowerUp(powerUp: PowerUpEntity): void {
    if (powerUp.collected) return;
    powerUp.collected = true;
    if (powerUp.type === 'ammo') {
      this.run.recordPowerUp('ammo');
      const added = this.session.addAmmo();
      this.emitAmmo('pickup');
      this.audio.play('ammoPickup');
      const message = added > 0 ? `+${added} AMMO` : 'AMMO FULL';
      this.showFloatingText(powerUp.view.x, powerUp.view.y, message, '#55d9e8');
      this.events.emit('ui:feedback', { message, tone: 'success' });
      this.events.emit('powerup:collected', { type: 'ammo', value: added });
      this.burst(powerUp.view.x, powerUp.view.y, 8, 0x55d9e8);
    } else {
      this.run.recordPowerUp('life');
      const result = this.session.addLife();
      if (result.scoreCompensation > 0) {
        this.score.bonus(result.scoreCompensation);
        this.run.recordBonusScore(result.scoreCompensation);
        this.emitScore();
      }
      this.emitLives('pickup');
      this.audio.play('lifePickup');
      const message = result.restored ? '+1 LIFE' : `MAX LIVES +${result.scoreCompensation}`;
      this.showFloatingText(powerUp.view.x, powerUp.view.y, message, '#ff7182');
      this.events.emit('ui:feedback', { message, tone: 'success' });
      this.events.emit('powerup:collected', {
        type: 'life',
        value: result.restored ? 1 : result.scoreCompensation,
      });
      this.burst(powerUp.view.x, powerUp.view.y, 10, 0xff7182);
    }
    this.deactivatePowerUp(powerUp, false);
  }

  private takeDamage(): void {
    if (!this.running || this.clockMs < this.invulnerableUntil) return;
    if (!this.session.takeDamage()) return;
    this.spawnElapsedMs = -750;
    this.invulnerableUntil = this.clockMs + GAMEPLAY_CONFIG.player.invulnerabilityMs;
    this.audio.play('hit');
    this.scene.cameras.main.flash(50, 255, 255, 255);
    this.emitLives('damage');
    if (this.session.snapshot.lives <= 0) {
      this.running = false;
      this.storage.save({ highScore: this.score.highScore });
      this.machine.transition(AppState.GameOver);
      this.events.emit('game:over', {
        score: this.score.score,
        highScore: this.score.highScore,
        run: this.run.finish(this.score.score),
      });
      this.audio.play('gameover');
      this.audio.stopMusic();
      this.ship.setVisible(false);
      this.burst(this.ship.x, this.ship.y, 18, 0xd7e8df);
    } else {
      this.machine.transition(AppState.Hit);
      if (!matchMedia('(prefers-reduced-motion: reduce)').matches) {
        this.scene.cameras.main.shake(100, 0.006);
      }
    }
  }

  private burst(x: number, y: number, count: number, color: number): void {
    let emitted = 0;
    for (const spark of this.sparks) {
      if (spark.active) continue;
      const angle = this.random.next() * Math.PI * 2;
      const speed = 35 + this.random.next() * 80;
      spark.active = true;
      spark.lifeMs = 260 + this.random.next() * 180;
      spark.velocityX = Math.cos(angle) * speed;
      spark.velocityY = Math.sin(angle) * speed;
      spark.view.setFillStyle(color).setPosition(x, y).setVisible(true).setAlpha(1);
      emitted += 1;
      if (emitted >= count) break;
    }
  }

  private updateSparks(delta: number): void {
    const seconds = delta / 1000;
    for (const spark of this.sparks) {
      if (!spark.active) continue;
      spark.lifeMs -= delta;
      spark.view.x += spark.velocityX * seconds;
      spark.view.y += spark.velocityY * seconds;
      spark.view.alpha = Phaser.Math.Clamp(spark.lifeMs / 300, 0, 1);
      if (spark.lifeMs <= 0) this.deactivateSpark(spark);
    }
  }

  private showFloatingText(x: number, y: number, message: string, color: string): void {
    const entity = this.floatingTexts.find((item) => !item.active);
    if (!entity) return;
    entity.active = true;
    entity.lifeMs = 850;
    entity.view
      .setText(message)
      .setColor(color)
      .setPosition(x, y - 15)
      .setAlpha(1)
      .setVisible(true);
  }

  private updateFloatingTexts(delta: number): void {
    for (const entity of this.floatingTexts) {
      if (!entity.active) continue;
      entity.lifeMs -= delta;
      entity.view.y -= delta * 0.018;
      entity.view.alpha = Phaser.Math.Clamp(entity.lifeMs / 500, 0, 1);
      if (entity.lifeMs <= 0) this.deactivateFloatingText(entity);
    }
  }

  private deactivateBullet(entity: BulletEntity): void {
    entity.active = false;
    entity.view.setVisible(false).setPosition(-40, -40);
  }

  private deactivateAsteroid(entity: AsteroidEntity): void {
    entity.active = false;
    entity.view.setVisible(false).setAlpha(1).setRotation(0).setPosition(-60, -60);
    entity.passed = false;
    entity.destroyed = false;
    entity.hp = 0;
    entity.variantId = '';
  }

  private deactivateSpark(entity: SparkEntity): void {
    entity.active = false;
    entity.velocityX = 0;
    entity.velocityY = 0;
    entity.lifeMs = 0;
    entity.view.setVisible(false).setAlpha(1).setPosition(-40, -40);
  }

  private deactivatePowerUp(entity: PowerUpEntity, emitDespawn: boolean): void {
    if (emitDespawn && entity.active) this.events.emit('powerup:despawned', { type: entity.type });
    entity.active = false;
    entity.collected = false;
    entity.phase = 0;
    entity.spawnedAt = 0;
    entity.view.setVisible(false).setAlpha(1).setScale(1).setRotation(0).setPosition(-50, -50);
  }

  private deactivateFloatingText(entity: FloatingTextEntity): void {
    entity.active = false;
    entity.lifeMs = 0;
    entity.view.setText('').setVisible(false).setAlpha(1).setPosition(-60, -60);
  }

  private updateDebugHitboxes(): void {
    if (!this.debugGraphics.visible) return;
    this.debugGraphics.clear().lineStyle(1, 0x55d9e8, 0.9);
    this.debugGraphics.strokeRect(this.ship.x - 19, this.ship.y - 7, 38, 14);
    this.debugGraphics.lineStyle(1, 0xff9b45, 0.85);
    for (const asteroid of this.asteroids) {
      if (!asteroid.active) continue;
      this.debugGraphics.strokeEllipse(
        asteroid.x + asteroid.hitboxOffsetX,
        asteroid.y + asteroid.hitboxOffsetY,
        asteroid.hitboxWidth,
        asteroid.hitboxHeight,
      );
    }
    this.debugGraphics.lineStyle(1, 0x8de05b, 0.9);
    for (const powerUp of this.powerUps) {
      if (powerUp.active)
        this.debugGraphics.strokeRect(powerUp.view.x - 10, powerUp.view.y - 10, 20, 20);
    }
  }

  private emitScore(): void {
    this.storage.save({ highScore: this.score.highScore });
    this.events.emit('game:score-changed', {
      score: this.score.score,
      highScore: this.score.highScore,
    });
  }

  private emitAmmo(reason: 'shot' | 'pickup' | 'reset'): void {
    const gameplay = this.session.snapshot;
    this.events.emit('player:ammo-changed', {
      ammo: gameplay.ammo,
      maxAmmo: gameplay.maxAmmo,
      reason,
    });
  }

  private emitLives(reason: 'damage' | 'pickup' | 'reset'): void {
    const gameplay = this.session.snapshot;
    this.events.emit('player:lives-changed', {
      lives: gameplay.lives,
      maxLives: gameplay.maxLives,
      reason,
    });
  }

  private emitHud(reason: 'reset'): void {
    this.emitLives(reason);
    this.emitAmmo(reason);
    this.emitScore();
  }

  private get activeAsteroids(): number {
    return this.asteroids.filter((item) => item.active).length;
  }

  private get activePowerUps(): number {
    return this.powerUps.filter((item) => item.active).length;
  }
}
